import React from 'react';
import { Link } from 'react-router-dom';
import { X, Plus, Minus, ShoppingBag, ArrowRight, Loader2 } from 'lucide-react';
import { useCart } from '../context/CartContext';

const formatPrice = (paiseStr, currency = 'INR') => {
  const rupees = parseInt(paiseStr || '0', 10) / 100;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(rupees);
};

// Free shipping threshold. Read from a future WP option if you want to
// make this admin-configurable; for now we hardcode the marketing default.
const FREE_SHIPPING_AT_PAISE = 40000; // ₹400

// Build Your Own Box items carry their flavor selection back from the
// Store API under extensions.raretto_build_box.flavors (see the
// raretto-headless.php plugin's Build-a-Box section).
const buildBoxFlavors = (item) => item.extensions?.raretto_build_box?.flavors;

const CartDrawer = () => {
  const {
    cartItems,
    updateItem,
    removeItem,
    drawerOpen,
    setDrawerOpen,
    cart,
    cartLoading,
    addingToCart,
  } = useCart();

  const totals = cart?.totals ?? {};
  const itemCount = cartItems.length;
  const subtotalPaise = parseInt(totals.total_items || '0', 10);
  const progress = Math.min(
    100,
    (subtotalPaise / FREE_SHIPPING_AT_PAISE) * 100
  );

  return (
    <>
      {drawerOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm"
          onClick={() => setDrawerOpen(false)}
        />
      )}
      <aside
        className={`fixed top-0 right-0 h-full w-full sm:w-[440px] bg-[#f6efe1] z-50 shadow-2xl transform transition-transform duration-500 ${
          drawerOpen ? 'translate-x-0' : 'translate-x-full'
        } flex flex-col`}
      >
        <div className="flex items-center justify-between px-7 py-6 border-b border-[#1a120b]/12">
          <p className="tracking-luxe">Your Box {itemCount}</p>
          <button onClick={() => setDrawerOpen(false)} aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {itemCount > 0 && (
          <div className="px-7 py-4 bg-[#efe6d3]">
            {subtotalPaise >= FREE_SHIPPING_AT_PAISE ? (
              <p className="text-sm text-[#1a120b]">
                <span className="text-[#b78d3c] font-medium">
                  Enjoy complimentary shipping
                </span>{' '}
                on the house.
              </p>
            ) : (
              <p className="text-sm text-[#1a120b]">
                Add{' '}
                <span className="font-medium">
                  {formatPrice(FREE_SHIPPING_AT_PAISE - subtotalPaise)}
                </span>{' '}
                more for free shipping
              </p>
            )}
            <div className="mt-2 h-1 bg-[#1a120b]/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#b78d3c] transition-all duration-500"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-7 py-5">
          {addingToCart && (
            <p className="flex items-center gap-2 text-xs text-[#1a120b]/50 mb-4">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Adding to your box…
            </p>
          )}
          {cartLoading ? (
            <p className="text-center text-[#1a120b]/50 py-16">
              Loading your box…
            </p>
          ) : itemCount === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center gap-4 py-16">
              <ShoppingBag className="w-10 h-10 text-[#1a120b]/40" />
              <p className="font-display text-2xl">Your box is empty</p>
              <p className="text-[#1a120b]/60 max-w-xs">
                Curate a signature box, or explore our bestsellers.
              </p>
              <Link
                to="/build-a-box"
                onClick={() => setDrawerOpen(false)}
                className="btn-primary mt-3"
              >
                Build a Box
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-[#1a120b]/12">
              {cartItems.map((item) => {
                const img =
                  item.images?.[0]?.thumbnail || item.images?.[0]?.src;
                const currency = item.prices?.currency_code || 'INR';
                const flavors = buildBoxFlavors(item);
                return (
                  <li key={item.key} className="py-5 flex gap-4">
                    <div className="w-20 h-20 rounded-full overflow-hidden bg-[#efe6d3] flex items-center justify-center flex-shrink-0">
                      {img ? (
                        <img
                          src={img}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="font-display text-2xl text-[#b78d3c]">
                          R
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between gap-2">
                        <p className="font-display text-lg leading-tight">
                          {item.name}
                        </p>
                        <button
                          onClick={() => removeItem(item.key)}
                          className="text-[#1a120b]/50 hover:text-[#1a120b]"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      {flavors?.length > 0 && (
                        <p className="text-xs text-[#1a120b]/60 mt-1">
                          {flavors.map((f) => `${f.count}× ${f.name}`).join(', ')}
                        </p>
                      )}
                      <div className="mt-3 flex items-center justify-between">
                        {flavors?.length > 0 ? (
                          // Quantity is locked to 1 for a custom box — the
                          // flavor breakdown describes one box's contents,
                          // so bumping quantity would make the label
                          // ambiguous for whoever packs the order. Add
                          // another box via the builder instead.
                          <span className="text-xs text-[#1a120b]/50 px-1">
                            Qty 1 · custom box
                          </span>
                        ) : (
                        <div className="flex items-center border border-[#1a120b]/25">
                          <button
                            onClick={() =>
                              updateItem({
                                key: item.key,
                                quantity: Math.max(1, item.quantity - 1),
                              })
                            }
                            className="w-7 h-7 flex items-center justify-center hover:bg-[#1a120b]/5"
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="w-8 text-center text-sm">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() =>
                              updateItem({
                                key: item.key,
                                quantity: item.quantity + 1,
                              })
                            }
                            className="w-7 h-7 flex items-center justify-center hover:bg-[#1a120b]/5"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        )}
                        <p className="font-medium">
                          {formatPrice(
                            item.totals?.line_total || item.prices?.price,
                            currency
                          )}
                        </p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {itemCount > 0 && (
          <div className="border-t border-[#1a120b]/12 px-7 py-6 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-[#1a120b]/70">Subtotal</span>
              <span>{formatPrice(totals.total_items)}</span>
            </div>
            {parseInt(totals.total_discount || '0', 10) > 0 && (
              <div className="flex justify-between text-sm text-[#b78d3c]">
                <span>Discount</span>
                <span>− {formatPrice(totals.total_discount)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-[#1a120b]/70">Shipping</span>
              <span>
                {parseInt(totals.total_shipping || '0', 10) === 0
                  ? 'Complimentary'
                  : formatPrice(totals.total_shipping)}
              </span>
            </div>
            <div className="gold-rule my-3"></div>
            <div className="flex justify-between font-display text-xl">
              <span>Total</span>
              <span>{formatPrice(totals.total_price)}</span>
            </div>
            <Link
              to="/checkout"
              onClick={() => setDrawerOpen(false)}
              className="btn-primary w-full mt-4"
            >
              Checkout <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/cart"
              onClick={() => setDrawerOpen(false)}
              className="block text-center text-xs tracking-widest uppercase text-[#1a120b]/60 hover:text-[#1a120b] mt-2"
            >
              View full cart
            </Link>
          </div>
        )}
      </aside>
    </>
  );
};

export default CartDrawer;
